
import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const CreateRoleOffice = () => {
    const [users, setUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState({}); 
    const [currentUserId] = useState(localStorage.getItem('id')); 
    const [newOfficeBranchName, setNewOfficeBranchName] = useState('');
    const [newOfficeBranchLatitude, setNewOfficeBranchLatitude] = useState('');
    const [newOfficeBranchLongitude, setNewOfficeBranchLongitude] = useState('');
    const [newBranchUnit, setNewBranchUnit] = useState('');
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [units, setUnits] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const [showUnitForm, setShowUnitForm] = useState(location.state?.form === 'unit');
    const [roleError, setRoleError] = useState('');
    const [rolesData, setRolesData] = useState([]);
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);

    useEffect(() => {
      const screenWidth = window.innerWidth;
      setIsSidebarVisible(screenWidth >= 520);
    }, []);

    const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);
    
    useEffect(() => {
        axiosInstance.get('/user/')
          .then(response => {
            const usersData = response.data.map(user => ({
              id: user.id,
              name: `${user.first_name} ${user.last_name}`,
              role: user.role,
              employeeCode: user.employee_code
            }));
            setUsers(usersData);
    
            axiosInstance.get(`/user/${currentUserId}/`)
              .then(response => {
                const userData = response.data;
                if (userData.role) {
                  setCurrentUser({ ...userData, role: userData.role.name });
                } else {
                  const fallbackRole = userData.role?.[0]?.name || 'guest';
                  setCurrentUser({ ...userData, role: fallbackRole });
                }
              })
              .catch(console.error);
          })
          .catch(console.error);
    }, []);

    useEffect(() => {
      axiosInstance.get(`/users/${currentUserId}/roles/`)
        .then(res => {
          setRolesData([res.data.active_role, res.data.assigned_role]);
        })
        .catch(console.error);
    }, []);

    const handleSwitchRole = async () => {
      try {
        await axiosInstance.post('/switch-role/', { role_id: rolesData[1].id });
        navigate('/user-dashboard');
      } catch (err) {
        setRoleError(err.response?.data?.detail || "Failed to switch role");
      }
    };

    useEffect(() => {
      axiosInstance.get('/branch-unit/')
        .then(response => {
          setUnits(response.data);
        })
        .catch(console.error);
    }, []);

    const handleCreateBranchUnit = () => {
      if (!newBranchUnit.trim()) {
        setError('Please enter a unit name');
        return;
      }
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      
      axiosInstance.post('/branch-unit/', { name: newBranchUnit })
        .then(response => {
          setSuccess('Unit created successfully!');
          setNewBranchUnit('');
          setTimeout(() => navigate('/roles-office'), 1500);
        })
        .catch(err => {
          console.error(err);
          setError('Failed to create unit. Please try again.');
        })
        .finally(() => setIsLoading(false));
    };

    const handleCreateOfficeBranch = () => {
      if (!newOfficeBranchName.trim()) {
        setError('Please enter an office branch name');
        return;
      }
      if (!selectedUnitId) {
        setError('Please select a unit');
        return;
      }
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      
      axiosInstance.post('/office-branch/', {
        name: newOfficeBranchName,
        latitude: newOfficeBranchLatitude,
        longitude: newOfficeBranchLongitude,
        unit: selectedUnitId
      })
      .then(response => {
        setSuccess('Office branch created successfully!');
        setNewOfficeBranchName('');
        setNewOfficeBranchLatitude('');
        setNewOfficeBranchLongitude('');
        setSelectedUnitId('');
        setTimeout(() => navigate('/roles-office'), 1500);
      })
      .catch(err => {
        console.error(err);
        if (err.response?.data) {
          const errorData = err.response.data;
          const errorMessages = Object.keys(errorData).map(key => `${key}: ${Array.isArray(errorData[key]) ? errorData[key].join(', ') : errorData[key]}`);
          setError(errorMessages.join(', '));
        } else {
          setError('Failed to create office branch. Please try again.');
        }
      })
      .finally(() => setIsLoading(false));
    };

    const handleLogout = () => {
      axiosInstance.post('/logout/', { refresh: localStorage.getItem('refresh') })
        .then(() => {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('employee_code');
          localStorage.removeItem('assignedRole');
          localStorage.removeItem('id'); 
          window.location.href = '/';
        })
        .catch(console.error);
    };

    return (
        <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
          <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
          <Sidebar 
            isSidebarVisible={isSidebarVisible} 
            currentUser={currentUser} 
            activeRole={currentUser?.role}
            error={roleError}
            handleSwitchRole={handleSwitchRole}
            handleLogout={handleLogout} 
          />
          
          <main className="dashboard-main-content">
            <div className="dashboard-header-section">
              <h1>{showUnitForm ? 'Create Unit' : 'Create Office Branch'}</h1>
              <p>Add a new {showUnitForm ? 'unit' : 'office branch'} to your organization</p>
            </div>

            <div className="create-role-card">
              {error && (
                <div className="form-error-message">
                  <span>!</span> {error}
                </div>
              )}
              
              {success && (
                <div className="form-success-message">
                  <span>&#10003;</span> {success}
                </div>
              )}

              {showUnitForm ? (
                <div className="form-section">
                  <div className="section-header">
                    <span className="section-icon">&#128193;</span>
                    <h3>Unit Information</h3>
                  </div>
                  
                  <div className="create-form-grid">
                    <div className="form-group">
                      <label htmlFor="newBranchUnit">Unit Name *</label>
                      <input 
                        type="text" 
                        id="newBranchUnit"
                        value={newBranchUnit} 
                        onChange={e => setNewBranchUnit(e.target.value)}
                        placeholder="Enter unit name"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      className="submit-btn" 
                      onClick={handleCreateBranchUnit}
                      disabled={isLoading}
                    >
                      {isLoading ? <span className="loading-spinner"></span> : <><span>+</span> Create Unit</>}
                    </button>
                    <button 
                      className="cancel-btn" 
                      onClick={() => navigate('/roles-office')}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="form-section">
                  <div className="section-header">
                    <span className="section-icon">&#127968;</span>
                    <h3>Office Branch Information</h3>
                  </div>
                  
                  <div className="create-form-grid">
                    <div className="form-group">
                      <label htmlFor="selectedUnitId">Unit *</label>
                      <select 
                        id="selectedUnitId"
                        value={selectedUnitId} 
                        onChange={e => setSelectedUnitId(e.target.value)}
                        required
                      >
                        <option value="">Select a Unit</option>
                        {units.map(unit => (
                          <option key={unit.id} value={unit.id}>{unit.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="newOfficeBranchName">Office Branch Name *</label>
                      <input 
                        type="text" 
                        id="newOfficeBranchName"
                        value={newOfficeBranchName} 
                        onChange={e => setNewOfficeBranchName(e.target.value)}
                        placeholder="Enter office branch name"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="newOfficeBranchLatitude">Latitude</label>
                      <input 
                        type="text" 
                        id="newOfficeBranchLatitude"
                        value={newOfficeBranchLatitude} 
                        onChange={e => setNewOfficeBranchLatitude(e.target.value)}
                        placeholder="Enter latitude"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="newOfficeBranchLongitude">Longitude</label>
                      <input 
                        type="text" 
                        id="newOfficeBranchLongitude"
                        value={newOfficeBranchLongitude} 
                        onChange={e => setNewOfficeBranchLongitude(e.target.value)}
                        placeholder="Enter longitude"
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      className="submit-btn" 
                      onClick={handleCreateOfficeBranch}
                      disabled={isLoading}
                    >
                      {isLoading ? <span className="loading-spinner"></span> : <><span>+</span> Create Office Branch</>}
                    </button>
                    <button 
                      className="cancel-btn" 
                      onClick={() => navigate('/roles-office')}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
   );  
};

export default CreateRoleOffice;


